import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { readWatchlist, toggleWatchlist, onWatchlistChange } from "@/lib/watchlist";

export const WatchlistDrawer = () => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(readWatchlist);
  useEffect(() => onWatchlistChange(() => setList(readWatchlist())), []);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <button data-testid="watchlist-open-button" onClick={() => setOpen(true)} aria-label="Watchlist" className="relative w-10 h-10 kp-glass flex items-center justify-center text-white/80 hover:text-[#FF2A55] transition-colors">
        <Heart size={16} className={list.length ? "fill-[#FF2A55] text-[#FF2A55]" : ""} />
        {list.length > 0 && (
          <span data-testid="watchlist-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[#00F0FF] text-[#08070C] text-[0.6rem] font-mono font-bold flex items-center justify-center">
            {list.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="fixed inset-0 z-[70] bg-[#08070C]/70 backdrop-blur-sm" />
            <motion.aside
              data-testid="watchlist-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-md bg-[#0F0D16] border-l border-white/[0.08] flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-20 border-b border-white/[0.06]">
                <div>
                  <span className="kp-eyebrow">Your</span>
                  <h2 className="font-display font-bold text-xl">Watchlist · {list.length}</h2>
                </div>
                <button data-testid="watchlist-close-button" onClick={() => setOpen(false)} className="p-2 text-[#94A3B8] hover:text-white transition-colors" aria-label="Close"><X size={20} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
                {list.length === 0 && (
                  <div data-testid="watchlist-empty" className="m-auto text-center px-8">
                    <Heart size={32} className="mx-auto text-[#64748B]" />
                    <p className="font-display mt-4">Your watchlist is empty</p>
                    <p className="text-[#64748B] text-sm mt-2">Tap the heart (♥) on any anime card to save it here.</p>
                  </div>
                )}
                {list.map((a, i) => (
                  <motion.div key={a.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} data-testid={`watchlist-item-${a.id}`} className="group flex items-center gap-4 p-2 hover:bg-white/[0.03] transition-colors">
                    <Link to={`/anime/${a.id}`} onClick={() => setOpen(false)} className="flex items-center gap-4 flex-1 min-w-0">
                      <img src={resolveMedia(a.cover_url)} alt="" className="w-12 h-16 object-cover kp-card-clip" />
                      <div className="min-w-0">
                        <div className="font-display text-sm font-semibold truncate">{a.title}</div>
                        <div className="text-[0.65rem] font-mono text-[#64748B] truncate uppercase tracking-wider">{a.genres.slice(0, 3).join(" · ")}</div>
                      </div>
                    </Link>
                    <button data-testid={`watchlist-remove-${a.id}`} onClick={() => toggleWatchlist(a)} className="p-2 text-[#64748B] hover:text-[#FF2A55] transition-colors" aria-label="Remove"><X size={15} /></button>
                  </motion.div>
                ))}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
